import React, { useState } from 'react';
import {
  Box, Paper, Typography, Stack, Button, Chip, Alert, CircularProgress, Table, TableHead, TableBody, TableRow,
  TableCell, Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Checkbox, IconButton,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CloneInclude, CloneSummary, RateCardList, RateCardVehicle, cloneZoneSettings, getRateCards,
} from '../../services/rateCardService';
import RateCardEditor from './RateCardEditor';
import ZonePicker from './ZonePicker';
import { useToast } from '../../lib/useToast';
import { getApiErrorMessage } from '../../lib/toast';
import { useConfirm } from '../../components/feedback/ConfirmDialog';
import { useCan } from '../../auth/Can';

const CLONE_ITEMS: Array<{ key: keyof CloneInclude; label: string; detail: string }> = [
  { key: 'rateCards', label: 'Rate cards for every vehicle', detail: 'Each vehicle\'s zone card, or "uses global" where the source has none. Also the old zone rates, so the kill switch prices both zones alike.' },
  { key: 'rushHour', label: 'Rush hour', detail: 'Window and multiplier.' },
  { key: 'providers', label: 'Map providers', detail: 'Geocode, places and route provider.' },
  { key: 'subscriptions', label: 'Driver subscription plans', detail: 'Plan price, ride limit and availability overrides.' },
  { key: 'surge', label: 'Manual surge multiplier', detail: 'Usually a temporary live-ops lever — off by default.' },
];
export const CLONE_DEFAULTS: CloneInclude = { rateCards: true, rushHour: true, providers: true, subscriptions: true, surge: false };

interface Props { zoneId: number; zoneName: string; city: string | null; onCloned?: () => void }

/** Zone page: every vehicle's card for this zone (edit = same editor + preview) and copy-from-zone. */
const ZoneRateCardsPanel: React.FC<Props> = ({ zoneId, zoneName, city, onCloned }) => {
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { can } = useCan();
  const canWrite = can('pricing:write');
  const list = useQuery({ queryKey: ['rate-cards'], queryFn: getRateCards });
  const data: RateCardList | undefined = list.data;
  const [editing, setEditing] = useState<RateCardVehicle | null>(null);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [sourceId, setSourceId] = useState<number | null>(null);
  const [include, setInclude] = useState<CloneInclude>(CLONE_DEFAULTS);
  const [summary, setSummary] = useState<CloneSummary | null>(null);

  const cloneMut = useMutation({
    mutationFn: () => cloneZoneSettings(zoneId, sourceId!, include),
    onSuccess: (s) => {
      setSummary(s);
      toast.success('Settings copied — new bookings in this zone use them within seconds');
      queryClient.invalidateQueries({ queryKey: ['rate-cards'] });
      queryClient.invalidateQueries({ queryKey: ['rate-card-scope'] });
      onCloned?.();
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  if (list.isLoading) return <Paper sx={{ p: 2, mb: 2 }}><CircularProgress size={20} /></Paper>;
  if (list.isError || !data) return <Alert severity="error" sx={{ mb: 2 }}>{getApiErrorMessage(list.error)}</Alert>;
  const source = sourceId ? data.zones.find((z) => z.id === sourceId) : null;
  const scopeLabel = `${zoneName}${city ? ` · ${city}` : ''}`;

  return (
    <Paper sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 2, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
        <ReceiptLongIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6">Rate cards (distance slabs)</Typography>
          <Typography variant="caption" color="text.secondary">Overrides the global card for rides picked up in this zone.</Typography>
        </Box>
        {canWrite && can('zones:write') && (
          <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => { setSummary(null); setSourceId(null); setInclude(CLONE_DEFAULTS); setCloneOpen(true); }}>
            Copy settings from another zone
          </Button>
        )}
      </Stack>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Vehicle</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>In this zone</TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {data.vehicles.map((v) => {
            const z = v.zones.find((c) => c.zoneId === zoneId && c.isActive);
            return (
              <TableRow key={v.rideOption.id}>
                <TableCell>{v.rideOption.label || v.rideOption.type}</TableCell>
                <TableCell>
                  {z ? <Chip size="small" color={z.isSeed ? 'default' : 'success'} label={z.isSeed ? 'Own card (old zone rates)' : `Own card · v${z.version}`} />
                     : <Chip size="small" variant="outlined" label="Uses the global card" />}
                </TableCell>
                <TableCell align="right"><Button size="small" onClick={() => setEditing(v)}>{canWrite ? 'Edit / preview' : 'View / preview'}</Button></TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ flex: 1 }}>Rate card · {editing?.rideOption.label} · {scopeLabel}</Box>
          <IconButton onClick={() => setEditing(null)} aria-label="Close"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {editing && (
            <RateCardEditor vehicle={editing} zoneId={zoneId} scopeLabel={scopeLabel} list={data} canWrite={canWrite} onRemoved={() => setEditing(null)} />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={cloneOpen} onClose={() => setCloneOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Copy settings into {zoneName}</DialogTitle>
        <DialogContent dividers>
          {summary ? (
            <Alert severity="success">
              Copied from {source?.name}: {summary.rateCardsCopied} rate card(s) copied, {summary.rateCardsRemoved} switched to global,
              {' '}{summary.zoneRatesCopied} old zone rate(s), {summary.subscriptionsCopied} subscription override(s)
              {summary.zoneFieldsCopied.length ? `, zone settings: ${summary.zoneFieldsCopied.join(', ')}` : ''}.
            </Alert>
          ) : (
            <Stack spacing={2}>
              <Typography variant="body2">Makes this zone price and behave exactly like the zone you pick. Name, boundary, city and active status are never copied.</Typography>
              {source
                ? <Alert severity="info" action={<Button size="small" onClick={() => setSourceId(null)}>Change</Button>}>Copy from: {source.name}{source.city ? ` · ${source.city}` : ''}</Alert>
                : <ZonePicker zones={data.zones} excludeIds={[zoneId]} onPick={setSourceId} label="Copy from zone" />}
              <Box>
                {CLONE_ITEMS.map((it) => (
                  <FormControlLabel key={it.key} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}
                    control={<Checkbox size="small" checked={include[it.key]} onChange={(e) => setInclude({ ...include, [it.key]: e.target.checked })} />}
                    label={<Box><Typography variant="body2" sx={{ fontWeight: 600 }}>{it.label}</Typography><Typography variant="caption" color="text.secondary">{it.detail}</Typography></Box>} />
                ))}
              </Box>
              <Alert severity="warning">Overwrites this zone's current settings for the ticked items. Rate-card history keeps every previous version. New bookings only.</Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneOpen(false)}>{summary ? 'Done' : 'Cancel'}</Button>
          {!summary && (
            <Button variant="contained" disabled={!sourceId || !Object.values(include).some(Boolean) || cloneMut.isPending} onClick={async () => {
              const ok = await confirm({ title: 'Copy zone settings', message: `Overwrite ${zoneName} with ${source?.name}'s settings for the ticked items?`, confirmText: 'Copy' });
              if (ok) cloneMut.mutate();
            }}>{cloneMut.isPending ? 'Copying…' : 'Copy'}</Button>
          )}
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ZoneRateCardsPanel;
